import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../lib/auth";
import { useCardsStore } from "../lib/store";
import { applyVcfImportOptions, isVcfFile, parseVcfFile, type ParsedVCF } from "../lib/vcf";
import { appendCards, detectTsubameSheet, ensureTagsColumn } from "../lib/sheets";
import { IMPORT_MAX_ROWS } from "../lib/constants";
import type { BusinessCard } from "../lib/types";

type Step = "upload" | "preview" | "running" | "done" | "error";

export default function VcfImportFlow() {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const sheet = useCardsStore((s) => s.sheet);
  const setSheet = useCardsStore((s) => s.setSheet);
  const setStatus = useCardsStore((s) => s.setStatus);

  const [step, setStep] = useState<Step>("upload");
  const [parsed, setParsed] = useState<ParsedVCF | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [appended, setAppended] = useState(0);
  const [bulkTagsInput, setBulkTagsInput] = useState("");
  const [fillTodayIfEmpty, setFillTodayIfEmpty] = useState(true);

  const handleFile = async (file: File) => {
    setError(null);
    if (!isVcfFile(file)) {
      setError(
        `この形式は対応していません: ${file.name || "(名前不明)"} — .vcf / .vcard ファイルを選択してください`,
      );
      return;
    }
    try {
      const result = await parseVcfFile(file);
      if (result.cards.length === 0) {
        setError(
          result.totalBlocks === 0
            ? "VCF にカードが見つかりません (BEGIN:VCARD ブロックなし)"
            : `VCF を読み込みましたが、取り込めるカードがありませんでした(${result.totalBlocks}件中 0件)`,
        );
        return;
      }
      if (result.cards.length > IMPORT_MAX_ROWS) {
        setError(
          `インポート可能件数の上限(${IMPORT_MAX_ROWS}件)を超えています(${result.cards.length}件)`,
        );
        return;
      }
      setParsed(result);
      setStep("preview");
    } catch (err) {
      setError(err instanceof Error ? err.message : "VCF 読み込みに失敗しました");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const runImport = async () => {
    if (!parsed || !accessToken) return;
    setStep("running");
    setError(null);

    try {
      let target = sheet;
      if (!target) {
        const detected = await detectTsubameSheet(accessToken);
        if (!detected) {
          throw new Error(
            "つばめカードのスプレッドシートが見つかりません。先に iOS 版でシートを作成してください。",
          );
        }
        target = detected;
        setSheet(detected);
      }

      await ensureTagsColumn(target.id, accessToken).catch(() => {});

      const bulkTags = bulkTagsInput
        .split(/[,、，;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);

      const cards = applyVcfImportOptions(parsed.cards, {
        bulkTags,
        fillTodayIfEmpty,
      });

      const { appended: count } = await appendCards(
        target.id,
        cards,
        accessToken,
      );
      setAppended(count);
      setStatus("idle");
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "インポートに失敗しました");
      setStep("error");
    }
  };

  // ============================================================
  // Render
  // ============================================================

  if (step === "upload") {
    return (
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="rounded-lg border-2 border-dashed border-washiDeep bg-white px-6 py-16 text-center"
      >
        <p className="text-sm font-medium text-navy">VCF ファイルをドロップ</p>
        <p className="mt-1 text-xs text-inkLight">または下のボタンから選択</p>
        <label className="btn-primary mt-6 inline-block cursor-pointer">
          ファイルを選択
          <input
            type="file"
            accept=".vcf,.vcard,text/vcard,text/x-vcard,text/directory"
            onChange={handleSelect}
            className="hidden"
          />
        </label>
        {error && (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <p className="mt-8 text-xs text-inkLight">
          対応形式: vCard 2.1 / 3.0 / 4.0(UTF-8 / Shift-JIS)
          <br />
          上限: {IMPORT_MAX_ROWS.toLocaleString()} 件
          <br />
          書き出し元: iOS 連絡先 / Google Contacts / LINE myBridge / Outlook など
        </p>
      </div>
    );
  }

  if (step === "preview" && parsed) {
    const previewCards = parsed.cards.slice(0, 5);

    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-washiDeep bg-white p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-navy">プレビュー(先頭 5 件)</h3>
            <span className="text-xs text-inkLight">
              読込件数{" "}
              <strong className="text-navy">
                {parsed.cards.length.toLocaleString()}
              </strong>{" "}
              件
              {parsed.totalBlocks !== parsed.cards.length &&
                ` / 元データ ${parsed.totalBlocks} 件`}
            </span>
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-washiDeep/60">
                <tr>
                  {[
                    "氏名",
                    "会社",
                    "部署",
                    "役職",
                    "電話",
                    "携帯",
                    "メール",
                    "住所",
                    "メモ",
                  ].map((h) => (
                    <th
                      key={h}
                      className="whitespace-nowrap px-2 py-1 text-left font-medium text-navy"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-washiDeep/50">
                {previewCards.map((c, i) => (
                  <tr key={i}>
                    {(
                      [
                        "name",
                        "company",
                        "department",
                        "title",
                        "phone",
                        "mobile",
                        "email1",
                        "address",
                        "memo",
                      ] as Array<keyof BusinessCard>
                    ).map((k) => {
                      const raw = c[k];
                      const value =
                        typeof raw === "string"
                          ? raw
                          : Array.isArray(raw)
                            ? raw.join(", ")
                            : raw == null
                              ? ""
                              : String(raw);
                      return (
                        <td
                          key={k}
                          className="max-w-[16rem] truncate whitespace-nowrap px-2 py-1 text-navySoft"
                          title={value}
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {parsed.errors.length > 0 && (
            <details className="mt-3 text-xs text-inkLight">
              <summary className="cursor-pointer">
                警告 {parsed.errors.length} 件 (クリックで表示)
              </summary>
              <ul className="mt-2 list-disc space-y-0.5 pl-5">
                {parsed.errors.slice(0, 20).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
                {parsed.errors.length > 20 && (
                  <li>... 他 {parsed.errors.length - 20} 件</li>
                )}
              </ul>
            </details>
          )}
        </div>

        <div className="rounded-lg border border-washiDeep bg-white p-4">
          <h3 className="text-sm font-medium text-navy">インポートオプション</h3>

          <div className="mt-4 space-y-4">
            <div>
              <label
                htmlFor="vcf-bulk-tags"
                className="block text-xs font-medium text-navySoft"
              >
                すべての行に追加するタグ(任意)
              </label>
              <input
                id="vcf-bulk-tags"
                type="text"
                value={bulkTagsInput}
                onChange={(e) => setBulkTagsInput(e.target.value)}
                placeholder="例: iPhone連絡先, 2026年5月"
                className="mt-1 w-full rounded-md border border-washiDeep bg-white px-3 py-2 text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy/30"
              />
              <p className="mt-1 text-[11px] text-inkLight">
                カンマ・空白・セミコロンで複数指定。VCF 内の CATEGORIES とマージされます。
              </p>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={fillTodayIfEmpty}
                onChange={(e) => setFillTodayIfEmpty(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-washiDeep text-navy focus:ring-navy/30"
              />
              <div>
                <span className="font-medium text-navy">
                  取得日が空の時は今日の日付を入れる
                </span>
                <p className="text-[11px] text-inkLight">
                  オフにすると、取得日は空のままになります(VCF には取得日の概念が無いため通常は ON 推奨)。
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              setParsed(null);
              setStep("upload");
            }}
            className="btn-secondary"
          >
            ファイルを選び直す
          </button>
          <button type="button" onClick={runImport} className="btn-primary">
            {parsed.cards.length} 件をインポート
          </button>
        </div>
      </div>
    );
  }

  if (step === "running") {
    return (
      <div className="px-6 py-16 text-center text-sm text-navySoft">
        Google Sheets に書き込んでいます...
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="rounded-lg border border-washiDeep bg-white px-6 py-12 text-center">
        <p className="text-lg font-medium text-navy">
          ✅ {appended.toLocaleString()} 件のインポートが完了しました
        </p>
        <p className="mt-2 text-xs text-inkLight">シートの末尾に追加されました。</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={() => {
              setParsed(null);
              setBulkTagsInput("");
              setAppended(0);
              setStep("upload");
            }}
            className="btn-secondary"
          >
            続けてインポート
          </button>
          {sheet && (
            <a
              href={`https://docs.google.com/spreadsheets/d/${sheet.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Google Sheets で確認 ↗
            </a>
          )}
          <button
            type="button"
            onClick={() => navigate("/cards")}
            className="btn-primary"
          >
            一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-6 py-12 text-center">
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={() => setStep("preview")}
            className="btn-secondary"
          >
            戻る
          </button>
          <button type="button" onClick={runImport} className="btn-primary">
            再試行
          </button>
        </div>
      </div>
    );
  }

  return null;
}
